"use client"

import { TimelineItem as TimelineItemType } from "@/types/types"
import { useEffect, useRef } from "react"
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react"
import { TimelineItem } from "./TimelineItem"

interface TimelineContentProps {
  items: TimelineItemType[]
  currentIndex: number
  onSlideChange?: (index: number) => void
  autoplay?: boolean
  autoplaySpeed?: number
  speed?: number
  arrows?: boolean
  dots?: boolean
  infinite?: boolean
  fade?: boolean
  onSliderRef?: (ref: any) => void
  showImageOnly?: boolean
}

export const TimelineContent = ({
  items,
  currentIndex,
  onSlideChange,
  autoplay = false,
  autoplaySpeed = 3000,
  speed = 500,
  arrows = false,
  dots = false,
  infinite = false,
  fade = false,
  onSliderRef,
  showImageOnly = false,
}: TimelineContentProps) => {
  const swiperRef = useRef<SwiperClass | null>(null)
  const isProgrammaticChange = useRef(false)

  // Sync Swiper with currentIndex
  useEffect(() => {
    if (swiperRef.current && !isProgrammaticChange.current && items.length > 0) {
      isProgrammaticChange.current = true
      swiperRef.current.slideTo(currentIndex, speed)
      setTimeout(() => {
        swiperRef.current?.updateAutoHeight()
        isProgrammaticChange.current = false
      }, speed + 100)
    }
  }, [currentIndex, items.length, speed])

  const handleSlideChange = (swiper: SwiperClass) => {
    swiper.updateAutoHeight()
    if (!isProgrammaticChange.current && onSlideChange) {
      onSlideChange(swiper.activeIndex)
    }
  }

  if (!items || items.length === 0) {
    return <div className="text-center py-8 text-gray-500">No content available.</div>
  }

  return (
    <div className="w-full">
      <div className="sm:hidden">
        <Swiper
          spaceBetween={0}
          slidesPerView={1}
          speed={speed}
          autoHeight={true}
          onSwiper={swiper => {
            swiperRef.current = swiper
            if (onSliderRef) {
              onSliderRef(swiper)
            }
            // Initialize to current index
            if (items.length > 0) {
              setTimeout(() => {
                swiper.slideTo(currentIndex, 0)
              }, 100)
            }
          }}
          onSlideChange={handleSlideChange}
          allowTouchMove={true}
          grabCursor={true}
          className="timeline-content-swiper"
        >
          {items.map((item, index) => (
            <SwiperSlide key={`timeline-item-${index}`}>
              <div className="transition-opacity duration-300">
                <TimelineItem item={item} showImage={showImageOnly} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Desktop: Static content (no Swiper) */}
      <div className="hidden sm:block">
        <div className="transition-opacity duration-300">
          <TimelineItem item={items[currentIndex]} showImage={showImageOnly} />
        </div>
      </div>
    </div>
  )
}
